import { useEffect, useRef, useCallback } from 'react';

/**
 * useIdleTimeout
 *
 * Fires `onIdle` after `idleMs` milliseconds of no user activity.
 * Fires `onWarning` `warningMs` milliseconds before the idle deadline
 * so the UI can show a "You'll be logged out soon" notice.
 * Calls `onActivity` whenever the user moves / types / clicks.
 *
 * Tracked events: mousemove, mousedown, keydown, touchstart, scroll, wheel.
 *
 * @param {object} options
 * @param {number}   options.idleMs      – total idle time before logout (default 5 min)
 * @param {number}   options.warningMs   – show warning this many ms before logout (default 30 s)
 * @param {Function} options.onIdle      – called when idle timer expires
 * @param {Function} options.onWarning   – called when warning window begins
 * @param {Function} options.onActivity  – called when activity resets the timer
 */
const useIdleTimeout = ({
  idleMs = 5 * 60 * 1000,      // 5 minutes
  warningMs = 30 * 1000,        // 30-second warning
  onIdle,
  onWarning,
  onActivity,
} = {}) => {
  const idleTimer   = useRef(null);
  const warnTimer   = useRef(null);
  const warned      = useRef(false);

  const clearTimers = useCallback(() => {
    clearTimeout(idleTimer.current);
    clearTimeout(warnTimer.current);
  }, []);

  const resetTimers = useCallback(() => {
    clearTimers();
    warned.current = false;

    if (onActivity) onActivity();

    // Warning fires (idleMs - warningMs) ms after last activity
    const warnDelay = idleMs - warningMs;
    if (warnDelay > 0) {
      warnTimer.current = setTimeout(() => {
        warned.current = true;
        if (onWarning) onWarning();
      }, warnDelay);
    }

    // Idle (logout) fires idleMs after last activity
    idleTimer.current = setTimeout(() => {
      if (onIdle) onIdle();
    }, idleMs);
  }, [idleMs, warningMs, onIdle, onWarning, onActivity, clearTimers]);

  useEffect(() => {
    const EVENTS = [
      'mousemove',
      'mousedown',
      'keydown',
      'touchstart',
      'scroll',
      'wheel',
    ];

    const handleActivity = () => resetTimers();

    EVENTS.forEach(evt => window.addEventListener(evt, handleActivity, { passive: true }));

    // Start the timer on mount
    resetTimers();

    return () => {
      EVENTS.forEach(evt => window.removeEventListener(evt, handleActivity));
      clearTimers();
    };
  }, [resetTimers, clearTimers]);

  return { resetTimers };
};

export default useIdleTimeout;
